import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

interface Project {
  id: string;
  title_en: string;
  category: string;
  status: string;
  location: string;
  beneficiaries: number;
  progress: number;
  created_at: string;
}

const CATEGORIES = ['Education', 'Healthcare', 'Infrastructure', 'Emergency Relief', 'Women Empowerment'];
const STATUSES = ['active', 'completed', 'paused'];

const emptyForm = {
  title_en: '', title_fr: '', title_de: '', title_ps: '', title_da: '',
  description_en: '', description_ps: '', description_da: '',
  category: 'Education', status: 'active', image_url: '',
  location: '', beneficiaries: 0, progress: 0,
};

export default function AdminProjectsPage() {
  const { isEditor } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const fetchProjects = async () => {
    const { data } = await supabase.from('projects').select('id, title_en, category, status, location, beneficiaries, progress, created_at').order('created_at', { ascending: false });
    setProjects(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchProjects(); }, []);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setShowModal(true); };
  const openEdit = (p: Project) => {
    setForm({ ...emptyForm, title_en: p.title_en, category: p.category, status: p.status, location: p.location ?? '', beneficiaries: p.beneficiaries ?? 0, progress: p.progress ?? 0 });
    setEditId(p.id);
    setShowModal(true);
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return;
    setUploading(true);
    setUploadProgress(0);
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
    const filePath = `projects/${fileName}`;
    const { error: uploadErr } = await supabase.storage
      .from('project-images')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });
    if (uploadErr) { setUploading(false); return; }
    const { data: urlData } = supabase.storage.from('project-images').getPublicUrl(filePath);
    if (urlData?.publicUrl) setForm(prev => ({ ...prev, image_url: urlData.publicUrl }));
    setUploadProgress(100);
    setTimeout(() => { setUploading(false); setUploadProgress(0); }, 600);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleRemoveImage = () => {
    setForm(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!form.title_en.trim()) return;
    setSaving(true);
    if (editId) {
      await supabase.from('projects').update({ ...form, updated_at: new Date().toISOString() }).eq('id', editId);
    } else {
      await supabase.from('projects').insert([form]);
    }
    setSaving(false);
    setShowModal(false);
    fetchProjects();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from('projects').delete().eq('id', deleteId);
    setDeleteId(null);
    fetchProjects();
  };

  const statusColor = (s: string) =>
    s === 'active' ? 'bg-green-100 text-green-700' : s === 'completed' ? 'bg-[#3B9EF5]/10 text-[#3B9EF5]' : 'bg-gray-100 text-gray-500';

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1B2838]">Projects</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage CAF projects and initiatives</p>
          </div>
          {isEditor && (
            <button onClick={openAdd} className="flex items-center gap-2 px-5 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] transition-colors whitespace-nowrap">
              <i className="ri-add-line"></i> Add Project
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">
              <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              Loading projects...
            </div>
          ) : projects.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-folder-open-line text-3xl"></i></div>
              <p className="text-sm">No projects yet. Add your first project!</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Title</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Progress</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Beneficiaries</th>
                  {isEditor && <th className="px-5 py-3"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {projects.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-[#1B2838]">{p.title_en}</div>
                      <div className="text-xs text-gray-400">{p.location}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">{p.category}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColor(p.status)}`}>{p.status}</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden w-20">
                          <div className="h-full bg-[#3B9EF5] rounded-full" style={{ width: `${p.progress}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500">{p.progress}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">{p.beneficiaries?.toLocaleString()}</td>
                    {isEditor && (
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          <button onClick={() => openEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer transition-colors">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => setDeleteId(p.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#1B2838]">{editId ? 'Edit Project' : 'Add New Project'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title (English) *</label>
                  <input value={form.title_en} onChange={e => setForm({ ...form, title_en: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]" placeholder="Project title in English" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title (French)</label>
                  <input value={form.title_fr} onChange={e => setForm({ ...form, title_fr: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]" placeholder="Titre en français" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title (German)</label>
                  <input value={form.title_de} onChange={e => setForm({ ...form, title_de: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]" placeholder="Titel auf Deutsch" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] cursor-pointer">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] cursor-pointer">
                    {STATUSES.map(s => <option key={s} className="capitalize">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Location</label>
                  <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]" placeholder="e.g. Kabul, Afghanistan" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Beneficiaries</label>
                  <input type="number" value={form.beneficiaries} onChange={e => setForm({ ...form, beneficiaries: Number(e.target.value) })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]" min={0} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Progress (%)</label>
                  <input type="number" value={form.progress} onChange={e => setForm({ ...form, progress: Math.min(100, Math.max(0, Number(e.target.value))) })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]" min={0} max={100} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Project Image</label>

                  {/* Upload Zone */}
                  {!form.image_url && (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        dragOver
                          ? 'border-[#3B9EF5] bg-[#3B9EF5]/5'
                          : 'border-gray-200 hover:border-[#3B9EF5]/50 hover:bg-gray-50'
                      }`}
                    >
                      {uploading ? (
                        <div className="space-y-3">
                          <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div className="bg-[#3B9EF5] h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress || 60}%` }}></div>
                          </div>
                          <p className="text-sm text-gray-500">Uploading image...</p>
                        </div>
                      ) : (
                        <>
                          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 rounded-full bg-[#3B9EF5]/10">
                            <i className="ri-image-add-line text-2xl text-[#3B9EF5]"></i>
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Drop your image here or <span className="text-[#3B9EF5] underline">browse</span>
                          </p>
                          <p className="text-xs text-gray-400">JPEG, PNG, WebP or GIF — max 5MB</p>
                        </>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}

                  {/* Image Preview */}
                  {form.image_url && (
                    <div className="relative rounded-xl overflow-hidden bg-gray-100 group">
                      <img src={form.image_url} alt="Project preview" className="w-full h-48 object-cover object-top" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2">
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-white text-gray-700 rounded-full text-xs font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap flex items-center gap-1">
                          <i className="ri-image-edit-line"></i> Replace
                        </button>
                        <button type="button" onClick={handleRemoveImage} className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-red-500 text-white rounded-full text-xs font-semibold cursor-pointer hover:bg-red-600 whitespace-nowrap flex items-center gap-1">
                          <i className="ri-delete-bin-line"></i> Remove
                        </button>
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFileSelect} className="hidden" />
                    </div>
                  )}

                  {/* URL Fallback */}
                  <div className="mt-2">
                    <label className="block text-xs text-gray-400 mb-1">Or paste an image URL instead:</label>
                    <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5]" placeholder="https://..." />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description (English)</label>
                  <textarea value={form.description_en} onChange={e => setForm({ ...form, description_en: e.target.value })} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#3B9EF5] resize-none" placeholder="Project description..." />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={handleSave} disabled={saving || uploading || !form.title_en.trim()} className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2">
                {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                {editId ? 'Save Changes' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1B2838] text-center mb-2">Delete Project?</h3>
            <p className="text-sm text-gray-500 text-center mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-50 whitespace-nowrap">Cancel</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-bold cursor-pointer hover:bg-red-600 whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
